import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Trash2,
  Edit2,
  BarChart3,
  Shield,
  Calendar,
  Shuffle,
  RefreshCw,
  Eye,
  X,
} from "lucide-react";

export const COMMUNICATION_POLLS_STORAGE_KEY = "communicationAdvancedPolls";

function loadPolls() {
  try {
    const raw = localStorage.getItem(COMMUNICATION_POLLS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function savePollsToStorage(polls) {
  try {
    localStorage.setItem(COMMUNICATION_POLLS_STORAGE_KEY, JSON.stringify(polls));
  } catch {
    /* ignore */
  }
}

const defaultForm = () => ({
  title: "",
  description: "",
  questionType: "single",
  options: ["Option A", "Option B"],
  anonymous: false,
  showResults: "after_vote",
  randomizeOrder: false,
  allowChangeVote: false,
  startDate: "",
  endDate: "",
  status: "draft",
  assignmentType: "all",
});

const LIKERT_DEFAULT = [
  "Strongly disagree",
  "Disagree",
  "Neutral",
  "Agree",
  "Strongly agree",
];

function PollForm({
  initial,
  onSave,
  onCancel,
  embedded,
}) {
  const mergeInitial = (src) => {
    if (!src) return defaultForm();
    const base = defaultForm();
    return {
      ...base,
      ...src,
      options:
        Array.isArray(src.options) && src.options.length > 0
          ? src.options
          : base.options,
    };
  };

  const [form, setForm] = useState(() => mergeInitial(initial));

  useEffect(() => {
    setForm(mergeInitial(initial));
  }, [initial]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleTypeChange = (questionType) => {
    let options = form.options;
    if (questionType === "likert") options = [...LIKERT_DEFAULT];
    else if (questionType === "stars") options = ["1", "2", "3", "4", "5"];
    else if (questionType === "single" || questionType === "multi") {
      options = options.length < 2 ? ["Option A", "Option B"] : options;
    }
    setForm((f) => ({ ...f, questionType, options }));
  };

  const addOption = () =>
    setForm((f) => ({
      ...f,
      options: [...f.options, `Option ${f.options.length + 1}`],
    }));

  const setOptionAt = (i, v) =>
    setForm((f) => {
      const next = [...f.options];
      next[i] = v;
      return { ...f, options: next };
    });

  const removeOption = (i) =>
    setForm((f) => ({
      ...f,
      options: f.options.filter((_, j) => j !== i),
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title?.trim()) return;
    const opts = form.options.map((o) => String(o).trim()).filter(Boolean);
    if (form.questionType !== "stars" && form.questionType !== "likert" && opts.length < 2) {
      return;
    }
    onSave({
      ...form,
      id: form.id ?? initial?.id,
      options: form.questionType === "stars" ? ["1", "2", "3", "4", "5"] : opts,
    });
  };

  const boxClass = embedded
    ? "rounded-lg border border-gray-200 bg-white p-4 dark:border-[#3C3C4C] dark:bg-[#1D1D1D]"
    : "";

  return (
    <form onSubmit={handleSubmit} className={`flex flex-col gap-4 ${boxClass}`}>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Poll title *</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setField("title", e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-redhalo-red/35"
          placeholder="e.g. Q4 engagement pulse"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Description / context</label>
        <textarea
          value={form.description}
          onChange={(e) => setField("description", e.target.value)}
          rows={3}
          className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-redhalo-red/35"
          placeholder="Optional instructions shown above the question"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Question type</label>
        <select
          value={form.questionType}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-redhalo-red/35"
        >
          <option value="single">Single choice</option>
          <option value="multi">Multiple choice (advanced)</option>
          <option value="likert">Likert scale (5)</option>
          <option value="stars">Star rating (1–5)</option>
        </select>
      </div>

      {(form.questionType === "single" || form.questionType === "multi") && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-600">Answer options</label>
            <button
              type="button"
              onClick={addOption}
              className="text-xs text-redhalo-navy hover:underline flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add option
            </button>
          </div>
          <div className="space-y-2">
            {form.options.map((opt, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => setOptionAt(i, e.target.value)}
                  className="flex-1 border border-gray-300 px-2 py-1.5 text-sm"
                />
                {form.options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeOption(i)}
                    className="text-red-600 p-1"
                    aria-label="Remove option"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {form.questionType === "likert" && (
        <p className="text-xs text-gray-500">
          Standard 5-point Likert labels are used. You can edit each label below.
        </p>
      )}
      {form.questionType === "likert" &&
        form.options.map((opt, i) => (
          <input
            key={i}
            type="text"
            value={opt}
            onChange={(e) => setOptionAt(i, e.target.value)}
            className="w-full border border-gray-300 px-2 py-1.5 text-sm"
          />
        ))}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-gray-200 pt-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.anonymous}
            onChange={(e) => setField("anonymous", e.target.checked)}
          />
          <Shield className="w-4 h-4 text-gray-500" />
          Anonymous responses
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.randomizeOrder}
            onChange={(e) => setField("randomizeOrder", e.target.checked)}
          />
          <Shuffle className="w-4 h-4 text-gray-500" />
          Randomize option order
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.allowChangeVote}
            onChange={(e) => setField("allowChangeVote", e.target.checked)}
          />
          <RefreshCw className="w-4 h-4 text-gray-500" />
          Allow change vote before close
        </label>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">When to show results</label>
        <select
          value={form.showResults}
          onChange={(e) => setField("showResults", e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 text-sm bg-white"
        >
          <option value="after_vote">After employee votes</option>
          <option value="after_close">After poll closes</option>
          <option value="admin_only">Admins only (summary in Manage)</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Start (optional)</label>
          <input
            type="datetime-local"
            value={form.startDate}
            onChange={(e) => setField("startDate", e.target.value)}
            className="w-full border border-gray-300 px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">End (optional)</label>
          <input
            type="datetime-local"
            value={form.endDate}
            onChange={(e) => setField("endDate", e.target.value)}
            className="w-full border border-gray-300 px-2 py-1.5 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Audience</label>
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="pollAudience"
              checked={form.assignmentType === "all"}
              onChange={() => setField("assignmentType", "all")}
            />
            All employees
          </label>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Initial status</label>
        <select
          value={form.status}
          onChange={(e) => setField("status", e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 text-sm bg-white"
        >
          <option value="draft">Draft (not visible)</option>
          <option value="active">Active (open)</option>
        </select>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="px-4 py-2 text-sm rh-btn-primary hover:opacity-95"
        >
          {initial?.id ? "Update poll" : "Save poll"}
        </button>
      </div>
    </form>
  );
}

/**
 * Advanced poll builder (mode=advanced) and manage list (mode=manage).
 */
export default function CommunicationPollsPanel({
  mode,
  onNavigateAdvanced,
  canCreatePolls = true,
  canEditPolls = true,
  canDeletePolls = true,
}) {
  const [polls, setPolls] = useState(loadPolls);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);

  useEffect(() => {
    savePollsToStorage(polls);
  }, [polls]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return polls;
    return polls.filter(
      (p) =>
        p.title?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
    );
  }, [polls, search]);

  const handleSavePoll = useCallback(
    (data) => {
      if (!canCreatePolls && !(data?.id && canEditPolls)) return;
      const id = data.id || Date.now();
      const row = {
        ...data,
        id,
        updatedAt: new Date().toISOString(),
        createdAt: data.createdAt || new Date().toISOString(),
        responseCount: data.responseCount ?? 0,
      };
      setPolls((prev) => {
        const idx = prev.findIndex((p) => p.id === id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...prev[idx], ...row };
          return next;
        }
        return [...prev, row];
      });
      setEditing(null);
      setShowFormModal(false);
    },
    [canCreatePolls, canEditPolls]
  );

  const handleDelete = (id) => {
    if (!canDeletePolls) return;
    if (!window.confirm("Delete this poll permanently?")) return;
    setPolls((prev) => prev.filter((p) => p.id !== id));
  };

  const handleStatus = (id, status) => {
    if (!canEditPolls) return;
    setPolls((prev) =>
      prev.map((p) => (p.id === id ? { ...p, status, updatedAt: new Date().toISOString() } : p))
    );
  };

  if (mode === "advanced") {
    return (
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <div className="flex justify-between items-center p-4 border-b border-gray-300 flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-redhalo-navy" />
              Advanced polls
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Configure question types, anonymity, visibility of results, and lifecycle.
            </p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <PollForm onSave={handleSavePoll} embedded />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      <div className="flex justify-between items-center p-4 border-b border-gray-300 flex-shrink-0 gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-redhalo-navy" />
            Manage polls
          </h3>
          <p className="text-xs text-gray-500 mt-1">Edit, close, or remove polls. Response counts are stored locally.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onNavigateAdvanced?.()}
            disabled={!canCreatePolls}
            className="flex items-center gap-2 rh-btn-primary px-3 py-2 text-sm hover:opacity-95"
          >
            <Plus className="w-4 h-4" />
            New advanced poll
          </button>
        </div>
      </div>

      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <input
          type="search"
          placeholder="Search polls..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {filtered.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No polls yet. Use <strong>Advanced polls</strong> to create one, or click &quot;New advanced poll&quot;.
          </p>
        ) : (
          <div className="space-y-2">
            {filtered.map((p) => (
              <div
                key={p.id}
                className="flex flex-wrap items-start justify-between gap-3 border border-gray-300 bg-gray-50 p-3 dark:border-[#3C3C4C] dark:bg-[#1D1D1D]"
              >
                <div className="min-w-0 flex-1">
                  <h4 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{p.title}</h4>
                  <p className="mt-0.5 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">{p.description || "—"}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="text-[10px] px-2 py-0.5 bg-gray-200 rounded">
                      {p.questionType === "single"
                        ? "Single"
                        : p.questionType === "multi"
                        ? "Multi"
                        : p.questionType === "likert"
                        ? "Likert"
                        : "Stars"}
                    </span>
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] ${
                        p.status === "active"
                          ? "border border-emerald-200/80 bg-emerald-50 text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-300"
                          : p.status === "draft"
                          ? "border border-amber-200/80 bg-amber-50 text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-300"
                          : "bg-gray-200 text-gray-700 dark:bg-[#2a2a2a] dark:text-gray-300"
                      }`}
                    >
                      {p.status || "draft"}
                    </span>
                    {p.anonymous && (
                      <span className="text-[10px] px-2 py-0.5 bg-redhalo-mint/40 text-redhalo-navy-dark">Anonymous</span>
                    )}
                    <span className="text-[10px] text-gray-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {p.endDate ? new Date(p.endDate).toLocaleString() : "No end date"}
                    </span>
                    <span className="text-[10px] text-gray-600">
                      Responses: {p.responseCount ?? 0}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 items-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (!canEditPolls) return;
                      setEditing(p);
                      setShowFormModal(true);
                    }}
                    disabled={!canEditPolls}
                    className="p-2 text-redhalo-navy hover:bg-redhalo-mint/40 border border-transparent hover:border-redhalo-teal/50"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  {p.status === "active" && (
                    <button
                      type="button"
                      onClick={() => handleStatus(p.id, "closed")}
                      disabled={!canEditPolls}
                      className="rounded border border-redhalo-orange/60 px-2 py-1 text-xs text-redhalo-orange transition hover:bg-orange-50 dark:hover:bg-[#2a2a2a]"
                    >
                      Close
                    </button>
                  )}
                  {p.status === "draft" && (
                    <button
                      type="button"
                      onClick={() => handleStatus(p.id, "active")}
                      disabled={!canEditPolls}
                      className="rounded border border-emerald-300 px-2 py-1 text-xs text-emerald-800 transition hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
                    >
                      Publish
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDelete(p.id)}
                    disabled={!canDeletePolls}
                    className="p-2 text-red-600 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showFormModal && editing && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/50 p-4">
          <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl dark:border-[#3C3C4C] dark:bg-[#1D1D1D]">
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2 dark:border-[#3C3C4C] dark:bg-[#1D1D1D]">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Edit poll</span>
              <button
                type="button"
                onClick={() => {
                  setShowFormModal(false);
                  setEditing(null);
                }}
                className="p-1 rounded hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <PollForm
                initial={editing}
                embedded
                onSave={(data) => handleSavePoll({ ...data, id: editing.id, createdAt: editing.createdAt })}
                onCancel={() => {
                  setShowFormModal(false);
                  setEditing(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
